/**
 * "How should it go in?" -- the three insert choices (core/arrange.ts), asked
 * when a song is added to a queue whose owner wants to be asked (Radio's On
 * air). One click chooses; "Always do this" stores the choice as the default,
 * which the owner's settings can change back to asking.
 */

import { useEffect, useState, useSyncExternalStore } from "react";
import { INSERT_CHOICES, type InsertMode } from "../core/arrange";
import type { QueueTrack } from "../core/player";
import { Modal } from "./EditDialogs";

interface Ask {
  tracks: QueueTrack[];
  /** Keeps the answer as the default ("Always do this"). */
  remember: (mode: InsertMode) => void;
  dropped: boolean;
  done: (mode: InsertMode | null) => void;
}

let current: Ask | null = null;
const listeners = new Set<() => void>();
const set = (next: Ask | null) => {
  current = next;
  listeners.forEach((fn) => fn());
};

/** Ask how the songs go in; null when the person cancels. */
export function askInsert({ tracks, remember, dropped }: { tracks: QueueTrack[]; remember: (mode: InsertMode) => void; dropped: boolean }): Promise<InsertMode | null> {
  current?.done(null);
  return new Promise((resolve) => set({ tracks, remember, dropped, done: resolve }));
}

export function InsertDialog() {
  const open = useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    () => current
  );
  return open ? <Choose key={open.tracks.map((t) => t.id).join()} ask={open} /> : null;
}

function Choose({ ask }: { ask: Ask }) {
  const [always, setAlways] = useState(false);
  const finish = (mode: InsertMode | null) => {
    if (mode && always) ask.remember(mode);
    set(null);
    ask.done(mode);
  };
  // 1, 2 or 3 chooses, as the buttons show.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const n = Number(e.key);
      if (n >= 1 && n <= INSERT_CHOICES.length && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        finish(INSERT_CHOICES[n - 1].id);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });
  const one = ask.tracks.length === 1 ? ask.tracks[0] : null;
  return (
    <Modal title={one ? `Add “${one.title}”` : `Add ${ask.tracks.length} songs`} onClose={() => finish(null)}>
      <div className="nv-box-body">
        {one && <p className="nv-dim">{one.artist}</p>}
        <div className="nv-reasons nv-insert-choices" role="group" aria-label="How it goes in">
          {INSERT_CHOICES.map((c, n) => (
            <button key={c.id} className="nv-reason nv-insert-choice" autoFocus={n === 0} onClick={() => finish(c.id)}>
              <kbd>{n + 1}</kbd>
              <b>{ask.dropped ? c.here : c.label}</b>
              <span>{c.hint}</span>
            </button>
          ))}
        </div>
        <label className="nv-check">
          <input type="checkbox" checked={always} onChange={(e) => setAlways(e.target.checked)} />
          <span>Always do this (you can change it in Settings)</span>
        </label>
      </div>
      <footer className="nv-box-foot">
        <span className="nv-spacer" />
        <button className="nv-btn" onClick={() => finish(null)}>Cancel</button>
      </footer>
    </Modal>
  );
}
